import { PageNotice } from '../../../shared/components/PageFeedback';
import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import {
  CreateExpenseDialog,
  ExpensesTable,
} from '@/modules/accounting/components';
import { getExpenseMetricConfig } from '../components/shared/accountingPage.config';
import { useCreateExpenseForm, useExpensesData } from '@/modules/accounting/hooks';
import {
  ACCOUNTING_PERIOD_OPTIONS,
  EXPENSE_RECIPIENT_FILTER_OPTIONS,
  EXPENSE_STATUS_OPTIONS,
  EXPENSE_TYPE_OPTIONS,
} from '@/modules/accounting/utils';
import AdaptiveFilterBar from '../../../shared/components/patterns/AdaptiveFilterBar';
import MetricGrid from '../../../shared/components/patterns/MetricGrid';
import PageFrame from '../../../shared/components/patterns/PageFrame';
import PageHeader from '../../../shared/components/patterns/PageHeader';
import PrimaryActionButton from '../../../shared/components/patterns/PrimaryActionButton';
import PageActionRow from '../../../shared/components/patterns/PageActionRow';
import PageDivider from '../../../shared/components/patterns/PageDivider';
import '../styles/accounting.css';
import { parseEntityDeepLink, clearEntityDeepLink } from '../../../shared/utils/entityDeepLink';

export default function ExpensesPage() {
  const { capabilities } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLink = parseEntityDeepLink(searchParams, { expenseId: 'expense' });
  const focusedExpenseId = deepLink.entityType === 'expense' ? deepLink.entityId : '';
  const periodFromParam = searchParams.get('periodFrom') || '';
  const periodToParam = searchParams.get('periodTo') || '';
  const expensesData = useExpensesData({ periodFrom: periodFromParam, periodTo: periodToParam });
  const moduleFilters = useRegisteredModuleFilters('expenses', expensesData.expenses, { staffId: expensesData.staff, supplierId: expensesData.suppliers });
  const filteredExpenses = moduleFilters.filterRows(expensesData.filteredExpenses);
  const expenseForm = useCreateExpenseForm({
    expenses: expensesData.expenses,
    onCreate: expensesData.createExpenseResult,
  });
  const hasLoadFailure =
    expensesData.expensesModuleDisabled ||
    expensesData.expensesAuthRequired ||
    expensesData.expensesUnavailable ||
    Boolean(expensesData.expensesTenantDatabaseError);
  const hasActiveFilters =
    expensesData.filters.selectedType !== 'tutti' ||
    expensesData.filters.selectedPeriod !== 'tutti' ||
    expensesData.filters.selectedStatus !== 'tutti' ||
    expensesData.filters.selectedRecipient !== 'tutti';
  const statusMessage = expensesData.expensesAuthRequired
    ? 'Sessione non autorizzata per il backend accounting. Le uscite non sono disponibili.'
    : expensesData.expensesModuleDisabled
      ? expensesData.expensesError || 'Modulo accounting non abilitato per questo workspace.'
      : expensesData.expensesTenantDatabaseError
        ? expensesData.expensesError || 'Database tenant accounting non disponibile.'
      : expensesData.expensesUnavailable
        ? 'Backend accounting non disponibile. Le uscite non sono state caricate.'
        : '';
  const visibleExpenses = focusedExpenseId
    ? filteredExpenses.filter((expense) => String(expense.id) === String(focusedExpenseId))
    : filteredExpenses;

  useEffect(() => {
    if (periodFromParam || periodToParam) expensesData.setFilter('selectedPeriod', 'dashboard_period');
  }, [expensesData.setFilter, periodFromParam, periodToParam]);

  const filterConfig = {
    label: 'Filtri uscite',
    search: {
      key: 'query',
      placeholder: 'Cerca uscite...',
    },
    groups: [
    {
      key: 'selectedType',
      label: 'Tipo di Uscita',
      selection: 'single',
      emptyValue: 'tutti',
      options: EXPENSE_TYPE_OPTIONS,
    },
    {
      key: 'selectedPeriod',
      label: 'Periodo',
      selection: 'single',
      emptyValue: 'tutti',
      options: [
        ...ACCOUNTING_PERIOD_OPTIONS,
        ...((periodFromParam || periodToParam) ? [{ value: 'dashboard_period', label: 'Periodo Dashboard' }] : []),
      ],
    },
    {
      key: 'selectedStatus',
      label: 'Stato',
      selection: 'single',
      emptyValue: 'tutti',
      options: EXPENSE_STATUS_OPTIONS,
    },
    {
      key: 'selectedRecipient',
      label: 'Destinatario',
      selection: 'single',
      emptyValue: 'tutti',
      options: EXPENSE_RECIPIENT_FILTER_OPTIONS,
    },
  ]};

  function resetFilters() {
    expensesData.setFilter('query', '');
    expensesData.setFilter('selectedType', 'tutti');
    expensesData.setFilter('selectedPeriod', 'tutti');
    expensesData.setFilter('selectedStatus', 'tutti');
    expensesData.setFilter('selectedRecipient', 'tutti');
    expensesData.clearOverviewFilter();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('expenseId');
    nextParams.delete('periodFrom');
    nextParams.delete('periodTo');
    setSearchParams(nextParams, { replace: true });
  }

  function clearFocusedExpense() {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('expenseId');
    setSearchParams(nextParams, { replace: true });
  }

  return (
    <PageFrame filterModel={moduleFilters} className="accounting-page">
      <PageHeader
        title="Uscite"
        description="Gestione centralizzata di spese, stipendi e pagamenti"
      />

      {statusMessage ? (
        <PageNotice role="alert" className="accounting-alert accounting-alert--warning wt-inline-error">
          {statusMessage}
          {expensesData.expensesError ? (
            <span className="accounting-alert__detail">Errore: {expensesData.expensesError}</span>
          ) : null}
        </PageNotice>
      ) : null}

      {!hasLoadFailure ? (
        <>
          {expensesData.expensesCatalogStatus === 'empty' ? (
            <div className="accounting-alert">
              Nessuna uscita registrata nel backend accounting.
            </div>
          ) : null}

          {hasActiveFilters ? (
            <div className="accounting-alert">
              Metriche calcolate sulle uscite filtrate: {expensesData.stats.totalCount} risultati.
              <span className="sr-only">
                {expensesData.stats.totalCount} spese nella vista corrente EUR {expensesData.stats.totalExpected}
              </span>
            </div>
          ) : null}

          {focusedExpenseId ? (
            <div className="accounting-alert">
              Vista focalizzata sull'uscita #{focusedExpenseId}.
              <button type="button" className="accounting-inline-button" onClick={clearFocusedExpense}>
                Mostra tutte
              </button>
            </div>
          ) : null}

          <MetricGrid
            metrics={getExpenseMetricConfig(expensesData.stats).map((metric) => ({
              ...metric,
              active: expensesData.activeOverviewFilter === metric.id,
              onClick: () => expensesData.setOverviewFilter(metric.id),
            }))}
          />

          <PageDivider />
          <AdaptiveFilterBar
            config={filterConfig}
            values={expensesData.filters}
            onChange={(key, value) => expensesData.setFilter(key, value)}
            onReset={resetFilters}
            filtersLabel="Filtri uscite"
          />
          <PageDivider />
          <PageActionRow>
            <PrimaryActionButton
              onClick={expenseForm.openDialog}
              disabled={hasLoadFailure}
              iconKey="actions.add"
            >
              Spesa immediata
            </PrimaryActionButton>
          </PageActionRow>

          <ExpensesTable
            expenses={visibleExpenses}
            staff={expensesData.staff}
            suppliers={expensesData.suppliers}
            onRegisterPayment={expensesData.registerExpensePayment}
            onEditExpense={expensesData.updateExpense}
            onVoidExpense={expensesData.voidExpense}
            canManageAccountingDestructiveActions={
              capabilities?.canManageAccountingDestructiveActions === true
            }
            focusedEntityId={focusedExpenseId}
            focusedMode={deepLink.mode}
            onClearFocus={() => setSearchParams(clearEntityDeepLink(searchParams, ['expenseId']), { replace: true })}
          />
        </>
      ) : null}

      <CreateExpenseDialog
        open={expenseForm.isOpen}
        onOpenChange={(open) => (open ? expenseForm.openDialog() : expenseForm.closeDialog())}
        staff={expensesData.staff}
        staffUnavailable={expensesData.staffUnavailable}
        suppliers={expensesData.suppliers}
        supplierUnavailable={expensesData.supplierUnavailable}
        formState={{
          form: expenseForm.form,
          onFieldChange: expenseForm.updateField,
          onCancel: expenseForm.closeDialog,
          onSubmit: expenseForm.submit,
          isSubmitting: expenseForm.isSubmitting,
        }}
      />
    </PageFrame>
  );
}
